import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let uniqueId = 0;

@Component({
  selector: 'fts-accessible-select',
  templateUrl: './accessible-select.component.html',
  styleUrls: ['./accessible-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AccessibleSelectComponent),
      multi: true,
    },
  ],
})
export class AccessibleSelectComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() displayWith: (option: any) => string = option => (option == null ? '' : String(option));
  @Input() compareWith: (a: any, b: any) => boolean = (a, b) => a === b;
  @Input() placeholder = '';
  @Input() invalid = false;
  @Input() isRequired = false;
  @Input() triggerClass = '';
  @Input() panelClass = '';
  @Input() ariaLabel: string | null = null;
  @Input() ariaLabelledby: string | null = null;
  @Input() ariaDescribedby: string | null = null;
  @Input() id = `fts-accessible-select-${uniqueId++}`;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.isOpen = false;
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Output() opened = new EventEmitter<void>();

  @ViewChild('trigger') trigger?: ElementRef<HTMLElement>;

  isOpen = false;
  activeIndex = -1;
  value: any = null;

  private _disabled = false;
  private typeahead = '';
  private typeaheadTimer: any;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private host: ElementRef<HTMLElement>) {}

  get listboxId(): string {
    return `${this.id}-listbox`;
  }

  get hasValue(): boolean {
    return this.value != null && this.options.some(o => this.compareWith(o, this.value));
  }

  get triggerLabel(): string {
    const selected = this.options.find(o => this.compareWith(o, this.value));
    return selected != null ? this.displayWith(selected) : this.placeholder;
  }

  get activeDescendant(): string | null {
    return this.isOpen && this.activeIndex > -1 ? this.optionId(this.activeIndex) : null;
  }

  optionId(index: number): string {
    return `${this.id}-option-${index}`;
  }

  isSelected(option: any): boolean {
    return this.compareWith(option, this.value);
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  focus(): void {
    this.trigger?.nativeElement.focus();
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }
    this.isOpen = true;
    const selectedIndex = this.options.findIndex(o => this.compareWith(o, this.value));
    this.activeIndex = selectedIndex > -1 ? selectedIndex : this.firstEnabledIndex();
    this.trigger?.nativeElement.focus();
    this.scrollActiveIntoView();
    this.opened.emit();
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.trigger?.nativeElement.focus();
  }

  selectOption(option: any): void {
    if (option == null || this.isDisabledOption(option)) {
      return;
    }
    this.value = option;
    this.onChange(option);
    this.onTouched();
    this.isOpen = false;
    this.trigger?.nativeElement.focus();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    const key = event.key;
    if (!this.isOpen) {
      if (key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      } else if (key.length === 1) {
        this.open();
        this.onTypeahead(key);
      }
      return;
    }
    switch (key) {
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        if (this.activeIndex > -1) {
          this.selectOption(this.options[this.activeIndex]);
        } else {
          this.close();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.activeIndex = this.firstEnabledIndex();
        this.scrollActiveIntoView();
        break;
      case 'End':
        event.preventDefault();
        this.activeIndex = this.lastEnabledIndex();
        this.scrollActiveIntoView();
        break;
      case 'Tab':
        this.close();
        break;
      default:
        if (key.length === 1) {
          this.onTypeahead(key);
        }
        break;
    }
  }

  onTriggerClick(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  onOptionClick(option: any, index: number, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.activeIndex = index;
    this.selectOption(option);
  }

  onOptionMouseenter(index: number): void {
    this.activeIndex = index;
  }

  onTriggerBlur(): void {
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  private moveActive(delta: number): void {
    const count = this.options.length;
    if (!count) {
      return;
    }
    let index = this.activeIndex;
    for (let i = 0; i < count; i++) {
      index = (index + delta + count) % count;
      if (!this.isDisabledOption(this.options[index])) {
        this.activeIndex = index;
        this.scrollActiveIntoView();
        return;
      }
    }
  }

  private firstEnabledIndex(): number {
    return this.options.findIndex(o => !this.isDisabledOption(o));
  }

  private lastEnabledIndex(): number {
    for (let i = this.options.length - 1; i >= 0; i--) {
      if (!this.isDisabledOption(this.options[i])) {
        return i;
      }
    }
    return -1;
  }

  private isDisabledOption(option: any): boolean {
    return !!option && typeof option === 'object' && option.disabled === true;
  }

  private onTypeahead(char: string): void {
    clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    const match = this.options.findIndex(o => this.displayWith(o).toLowerCase().startsWith(this.typeahead));
    if (match > -1) {
      this.activeIndex = match;
      this.scrollActiveIntoView();
    }
    this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 500);
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      const el = document.getElementById(this.optionId(this.activeIndex));
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  }
}
